import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt } from "@fortawesome/free-solid-svg-icons";

const STORAGE_KEY = "securityNoticeLastShown";
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
// Fired on dismiss so modals queued behind this one (account-type onboarding) know it is gone.
export const SECURITY_NOTICE_CLOSED_EVENT = "ap:security-notice-closed";
// True while the weekly notice would show for a partner without 2FA (same rule as the effect below).
export const securityNoticeDue = () => {
  const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
  return !last || Date.now() - last > WEEK_MS;
};

// One-time-per-week announcement shown to any signed-in partner until they
// enable 2FA. Once 2FA is on it never shows again.
const SecurityAnnouncementModal = () => {
  const navigate = useNavigate();
  const twoFactor = useSelector((s) => s.userReducer.twoFactor);
  const authenticated = useSelector((s) => s.userReducer.authenticated);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!authenticated || !twoFactor.loaded) return;
    if (twoFactor.enabled) {
      setShow(false);
      return;
    }
    if (securityNoticeDue()) {
      setShow(true);
    }
  }, [authenticated, twoFactor.loaded, twoFactor.enabled]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
    window.dispatchEvent(new Event(SECURITY_NOTICE_CLOSED_EVENT));
  };

  if (!show) return null;

  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.62)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 20,
  };
  const card = {
    width: "100%",
    maxWidth: 520,
    background: "#141716",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 16,
    padding: 26,
    boxShadow: "0 20px 60px rgba(0,0,0,.5)",
  };
  const iconWrap = {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(58,209,126,.14)",
    color: "#3ad17e",
    fontSize: 20,
    flex: "none",
  };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={iconWrap}>
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
          <h2 style={{ margin: 0, fontSize: 20, color: "#eef3f0" }}>
            Major security update
          </h2>
        </div>

        <p style={{ color: "#c3ccc6", fontSize: 14, lineHeight: 1.6, margin: "0 0 12px" }}>
          We have rolled out a major security upgrade to the partner dashboard:
        </p>
        <ul style={{ color: "#c3ccc6", fontSize: 14, lineHeight: 1.7, margin: "0 0 16px", paddingLeft: 20 }}>
          <li>
            <b style={{ color: "#eef3f0" }}>Two-factor authentication (2FA)</b> is now
            available on your account.
          </li>
          <li>
            As part of enabling 2FA, some linked wallets were unlinked and will need
            to be <b style={{ color: "#eef3f0" }}>re-linked after you enable 2FA</b>.
          </li>
          <li>
            Changing your withdrawal wallet and requesting a withdrawal now{" "}
            <b style={{ color: "#eef3f0" }}>require 2FA</b>.
          </li>
        </ul>

        <div className="notice" style={{ marginBottom: 4 }}>
          <span className="ni">🔒</span>
          <div>
            <b>What you need to do:</b> sign in with your usual login wallet, then open{" "}
            <b>Security</b> and enable 2FA. Once 2FA is enabled you can re-link any
            wallets and manage your withdrawal wallet and withdrawals.
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button
            type="button"
            className="btnPrimary"
            style={{ flex: 1, height: 46 }}
            onClick={() => {
              dismiss();
              navigate("/security");
            }}
          >
            Set up 2FA now
          </button>
          <button
            type="button"
            className="btnGhost"
            style={{ flex: "none", height: 46, padding: "0 20px" }}
            onClick={dismiss}
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityAnnouncementModal;
